import { useCallback, useEffect, useMemo, useState } from 'react';
import { ResolvedInvoiceStatusDto } from '@tiendanube/common/src/domains/orders/commons';
import {
  InvoiceOperation,
  ResolvedInvoiceStatus,
} from '@tiendanube/common/src/enums';
import { useAppDispatch } from 'App/store';
import {
  resolveInvoices,
  updateInvoiceById,
} from '../../ordersSlice/orderSlice';

interface CancelInvoiceParams {
  orderId: string;
  fulfillmentOrderId: string;
}

function useCancelInvoiceOnOrderCancel({
  orderId,
  fulfillmentOrderId,
}: CancelInvoiceParams) {
  const dispatch = useAppDispatch();
  const [isCancellingInvoice, setIsCancellingInvoice] = useState(false);
  const [error, setError] = useState<null | Error>(null);
  const [invoices, setInvoices] = useState<ResolvedInvoiceStatusDto | null>(
    null,
  );
  const fetchResolvedInvoices = useCallback(async () => {
    try {
      const resolved = await dispatch(
        resolveInvoices({ orderId, fulfillmentOrderId }),
      ).unwrap();

      setInvoices(resolved);
    } catch (err: any) {
      setError(err);
    }
  }, [dispatch, orderId, fulfillmentOrderId]);

  useEffect(() => {
    fetchResolvedInvoices();
  }, [fetchResolvedInvoices]);

  const currentInvoice = useMemo(() => {
    if (!invoices?.invoices?.length) return null;

    return invoices.invoices[0];
  }, [invoices]);

  const canCancelInvoice = useMemo(
    () =>
      !!invoices &&
      invoices.status !== ResolvedInvoiceStatus.ALL_CANCELLED &&
      invoices.status !== ResolvedInvoiceStatus.ERROR_ON_EMIT &&
      invoices.status !== ResolvedInvoiceStatus.EMPTY_DOCUMENTS &&
      invoices.status !== ResolvedInvoiceStatus.CERT_ERROR,
    [invoices],
  );

  const cancelInvoice = useCallback(
    async (reason: string) => {
      if (!canCancelInvoice || isCancellingInvoice) return false;
      try {
        setIsCancellingInvoice(true);
        setError(null);

        await dispatch(
          updateInvoiceById({
            orderId,
            fulfillmentOrderId,
            reason: reason,
            eventType: InvoiceOperation.CANCELLATION,
          }),
        ).unwrap();

        await fetchResolvedInvoices();
        return true;
      } catch (err: any) {
        setError(err);
        return false;
      } finally {
        setIsCancellingInvoice(false);
      }
    },
    [
      dispatch,
      orderId,
      fulfillmentOrderId,
      isCancellingInvoice,
      canCancelInvoice,
      fetchResolvedInvoices,
    ],
  );

  return {
    cancelInvoice,
    isCancellingInvoice,
    error,
    invoices,
    canCancelInvoice,
    currentInvoice,
  };
}

export default useCancelInvoiceOnOrderCancel;
